import { useState, useEffect, useRef } from "react";
import axios from "axios";
import html2canvas from "html2canvas";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Calculator, Ruler, Package, FileText, Printer, Download, Trash2, ChevronDown, ChevronUp, Save, FolderOpen, FileText as FileIcon, CheckCircle2, XCircle, ArrowLeft, Plus, FileDown, Mail } from "lucide-react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import ProjectSupportDialog from "@/components/ProjectSupportDialog";
import QuoteTargetSelector from "@/components/QuoteTargetSelector";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Add auth headers to axios
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    headers: {
      Authorization: `Bearer ${token}`
    }
  };
};

const ConfiguratorPage = ({ onBack, selectedQuote, backLabel = "Back to Pedestrian Entrance Control" }) => {
  const [projectName, setProjectName] = useState("");
  const [quoteReference, setQuoteReference] = useState("");
  const [currentConfigId, setCurrentConfigId] = useState(null);
  const [priceModifier, setPriceModifier] = useState("");
  const [userRole, setUserRole] = useState("");
  const [quoteTarget, setQuoteTarget] = useState(""); // "distributor" or "installer"
  const [installerOnlyCapture, setInstallerOnlyCapture] = useState(false);
  const [banks, setBanks] = useState([{
    id: 1,
    totalLanes: "",
    ddaLanes: "",
    highWings: "",
    wingHeight: "",
    requireBases: "",
    requireAccessControl: "",
    credentialType: "",
    extendedWarranty: "",
    warrantyYears: "",
    requireBalustrade: "",
    balustradeMode: "", // "auto" or "manual"
    totalSpaceWidth: "",
    manualUprights: "",
    manualInfills: [], // Array of {id, width} for manual infill panels
    isOpen: true
  }]);
  const [result, setResult] = useState(null);
  // Bases have no reliable price yet (POA) — strip base costs from the on-screen BOM/totals.
  const BASE_PRICING_EMAIL = "uk.office@zkteco.co.uk";
  const stripBaseCosts = (data) => {
    if (!data || !Array.isArray(data.combined_bom)) return data;
    const isBase = (it) => String(it?.category || "").trim().toLowerCase() === "base";
    let dist = 0, inst = 0, eu = 0;
    const combined_bom = data.combined_bom.map((it) => {
      if (!isBase(it)) return it;
      dist += it.line_total || 0;
      inst += it.installer_total || 0;
      eu += it.end_user_total || 0;
      return { ...it, poa: true, unit_price: 0, line_total: 0, installer_price: 0, installer_total: 0, end_user_price: 0, end_user_total: 0 };
    });
    const role = (data.user_role || "").toLowerCase();
    // trade_subtotal / subtotal_with_delivery are shown in the price basis for the viewer's role
    const tradeBasis = role === "installer" ? inst : (role === "end_user" ? eu : dist);
    const sub = (v, amt) => (typeof v === "number" ? v - amt : v);
    // Strip base from per-bank subtotals too (bank_subtotal is in the viewer's basis)
    const banks = Array.isArray(data.banks) ? data.banks.map((bk) => {
      if (!Array.isArray(bk.bom)) return bk;
      let bBasis = 0;
      const bom = bk.bom.map((it) => {
        if (String(it?.category || "").trim().toLowerCase() !== "base") return it;
        bBasis += role === "installer" ? (it.installer_total || 0) : (role === "end_user" ? (it.end_user_total || 0) : (it.line_total || 0));
        return { ...it, poa: true, unit_price: 0, line_total: 0, installer_price: 0, installer_total: 0, end_user_price: 0, end_user_total: 0 };
      });
      return { ...bk, bom, bank_subtotal: sub(bk.bank_subtotal, bBasis) };
    }) : data.banks;
    return {
      ...data,
      combined_bom,
      banks,
      trade_subtotal: sub(data.trade_subtotal, tradeBasis),
      installer_subtotal: sub(data.installer_subtotal, inst),
      subtotal_with_delivery: sub(data.subtotal_with_delivery, tradeBasis),
      installer_subtotal_with_delivery: sub(data.installer_subtotal_with_delivery, inst),
      final_installer_price: sub(data.final_installer_price, inst),
      final_total: sub(data.final_total, eu),
      distributor_margin: sub(data.distributor_margin, inst - dist),
    };
  };
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [savedConfigs, setSavedConfigs] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [lostReason, setLostReason] = useState('');
  const [showProjectSupport, setShowProjectSupport] = useState(false);
  const [supportDeclined, setSupportDeclined] = useState(false);
  
  // Sundry Items state
  const [sundryItems, setSundryItems] = useState([]);
  const [showSundryDialog, setShowSundryDialog] = useState(false);
  const [editingSundryIndex, setEditingSundryIndex] = useState(null);
  const [sundryForm, setSundryForm] = useState({
    sku: '',
    description: '',
    quantity: 1,
    unit_price: 0
  });
  
  // Quote box ref for download
  const quoteBoxRef = useRef(null);

  const handleDownloadQuoteImage = async (installerOnly = false) => {
    if (!quoteBoxRef.current) return;
    try {
      if (installerOnly) {
        setInstallerOnlyCapture(true);
        await new Promise(r => setTimeout(r, 250));
      }
      const canvas = await html2canvas(quoteBoxRef.current, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false
      });
      const suffix = installerOnly ? 'Installer' : (isDistributorUser ? 'Distributor' : '');
      const link = document.createElement('a');
      link.download = `${projectName || 'Comet'}${suffix ? '_' + suffix : ''}_Quote.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      console.error('Error downloading quote:', err);
      setError('Failed to download quote image');
    } finally {
      if (installerOnly) setInstallerOnlyCapture(false);
    }
  };

  // Sundry Items Functions
  const handleAddSundryItem = () => {
    setSundryForm({ sku: '', description: '', quantity: 1, unit_price: 0 });
    setEditingSundryIndex(null);
    setShowSundryDialog(true);
  };

  const handleEditSundryItem = (index) => {
    setSundryForm(sundryItems[index]);
    setEditingSundryIndex(index);
    setShowSundryDialog(true);
  };

  const handleSaveSundryItem = () => {
    if (!sundryForm.description || sundryForm.quantity <= 0) {
      setError('Please enter a description and valid quantity');
      return;
    }
    
    const newItem = {
      ...sundryForm,
      quantity: parseFloat(sundryForm.quantity) || 1,
      unit_price: parseFloat(sundryForm.unit_price) || 0,
      line_total: (parseFloat(sundryForm.quantity) || 1) * (parseFloat(sundryForm.unit_price) || 0)
    };
    
    if (editingSundryIndex !== null) {
      const updated = [...sundryItems];
      updated[editingSundryIndex] = newItem;
      setSundryItems(updated);
    } else {
      setSundryItems([...sundryItems, newItem]);
    }
    
    setShowSundryDialog(false);
    setSundryForm({ sku: '', description: '', quantity: 1, unit_price: 0 });
    setEditingSundryIndex(null);
  };

  const handleDeleteSundryItem = (index) => {
    if (window.confirm('Remove this sundry item?')) {
      setSundryItems(sundryItems.filter((_, i) => i !== index));
    }
  };

  const getSundryTotal = () => {
    return sundryItems.reduce((sum, item) => sum + (item.line_total || 0), 0);
  };

  const handleNewQuote = async () => {
    console.log('New Quote button clicked');
    
    // Check if there's any data entered
    const hasData = projectName || banks.some(b => b.totalLanes || b.ddaLanes);
    
    // Check if current work is saved
    const isSaved = currentConfigId && result; // Has been calculated and has an ID
    
    if (hasData && !isSaved) {
      // User has unsaved work
      const response = window.confirm(
        "You have unsaved changes.\n\n" +
        "Click OK to save this quote first, or Cancel to discard and start new."
      );
      
      if (response) {
        // User wants to save first
        console.log('User chose to save before starting new quote');
        if (!projectName) {
          setError("Please enter a project name before saving");
          return;
        }
        // Trigger calculation/save
        setError("Please click 'Calculate & Save' to save this quote first, then click 'New Quote' again.");
        return;
      } else {
        // User wants to discard
        console.log('User chose to discard and start new');
      }
    } else if (hasData && isSaved) {
      // Work is saved, just confirm
      const confirmed = window.confirm("Start a new quote?\n\nYour current quote is saved.");
      if (!confirmed) {
        console.log('User cancelled new quote');
        return;
      }
    }
    
    console.log('Clearing form to start new quote...');
    
    // Close saved projects panel if open
    setShowSaved(false);
    
    // Reset all state to initial values
    setProjectName("");
    setQuoteReference("");
    setCurrentConfigId(null);
    setPriceModifier("");
    setBanks([{
      id: 1,
      totalLanes: "",
      ddaLanes: "",
      highWings: "no",
      wingHeight: "",
      extendedWarranty: "",
      warrantyYears: "",
      isOpen: true
    }]);
    setResult(null);
    setError("");
    setSupportDeclined(false); // Reset support declined flag
    setSundryItems([]); // Clear sundry items
    setSuccess("✓ New quote started! Enter project details below.");
    setTimeout(() => setSuccess(""), 4000);
    
    console.log('Form cleared - ready for new quote');
  };

  const addBank = () => {
    const newId = banks.length > 0 ? Math.max(...banks.map(b => b.id)) + 1 : 1;
    setBanks([...banks, {
      id: newId,
      totalLanes: "",
      ddaLanes: "",
      highWings: "",
      wingHeight: "",
      requireBases: "",
      requireAccessControl: "",
      credentialType: "",
      extendedWarranty: "",
      warrantyYears: "",
      requireBalustrade: "",
      balustradeMode: "",
      totalSpaceWidth: "",
      manualUprights: "",
      manualInfills: [],
      isOpen: true
    }]);
  };

  const removeBank = (id) => {
    if (banks.length === 1) {
      setError("You must have at least one bank");
      return;
    }
    setBanks(banks.filter(b => b.id !== id));
  };

  const updateBank = (id, field, value) => {
    setBanks(banks.map(b => {
      if (b.id === id) {
        if (field === 'highWings' && value === 'no') {
          return { ...b, [field]: value, wingHeight: "" };
        }
        if (field === 'highWings' && value === 'yes') {
          // Auto-select 1.8m since it's the only option now
          return { ...b, [field]: value, wingHeight: "1.8m" };
        }
        if (field === 'extendedWarranty' && value === 'no') {
          return { ...b, [field]: value, warrantyYears: "" };
        }
        if (field === 'requireAccessControl' && value === 'no') {
          return { ...b, [field]: value, credentialType: "" };
        }
        if (field === 'requireBalustrade' && value === 'no') {
          return { ...b, [field]: value, balustradeMode: "", totalSpaceWidth: "", manualUprights: "", manualInfills: [] };
        }
        if (field === 'balustradeMode' && value === 'auto') {
          return { ...b, [field]: value, manualUprights: "", manualInfills: [] };
        }
        if (field === 'balustradeMode' && value === 'manual') {
          return { ...b, [field]: value, totalSpaceWidth: "", manualInfills: [{ id: 1, width: "", qty: "1" }] };
        }
        return { ...b, [field]: value };
      }
      return b;
    }));
  };

  // Helper to add manual infill to a bank
  const addManualInfill = (bankId) => {
    setBanks(banks.map(b => {
      if (b.id === bankId) {
        const newId = b.manualInfills.length > 0 ? Math.max(...b.manualInfills.map(i => i.id)) + 1 : 1;
        return { ...b, manualInfills: [...b.manualInfills, { id: newId, width: "", qty: "1" }] };
      }
      return b;
    }));
  };

  // Helper to remove manual infill from a bank
  const removeManualInfill = (bankId, infillId) => {
    setBanks(banks.map(b => {
      if (b.id === bankId) {
        return { ...b, manualInfills: b.manualInfills.filter(i => i.id !== infillId) };
      }
      return b;
    }));
  };

  // Helper to update manual infill
  const updateManualInfill = (bankId, infillId, field, value) => {
    setBanks(banks.map(b => {
      if (b.id === bankId) {
        return {
          ...b,
          manualInfills: b.manualInfills.map(i => i.id === infillId ? { ...i, [field]: value } : i)
        };
      }
      return b;
    }));
  };

  // Calculate balustrade requirements
  // R50 Frame (ACC-TSA-R50) = 2 uprights (25mm each = 50mm) - £201.80
  // Infill Panel (ACC-TSA-AB-R50-EXT) = custom width up to 1m - £165
  // Each infill needs its own R50 frame (speedgate positioned centrally with infills either side)
  const calculateBalustrade = (gapMm) => {
    if (gapMm <= 0) return { r50Count: 0, infillCount: 0, uprights: 0, infillWidth: 0 };
    
    const UPRIGHT_WIDTH = 25; // mm per upright
    const UPRIGHTS_PER_R50 = 2; // Each R50 has 2 uprights
    const R50_WIDTH = UPRIGHT_WIDTH * UPRIGHTS_PER_R50; // 50mm per R50
    const MAX_INFILL_WIDTH = 1000; // mm (1 meter max)
    
    // Each infill needs its own R50 frame
    // gap = num_infills * (50mm R50 + infill_width)
    
    let numInfills = 1;
    let infillWidth = gapMm - R50_WIDTH; // Start with 1 infill
    
    while (infillWidth > MAX_INFILL_WIDTH) {
      numInfills++;
      const totalR50Width = numInfills * R50_WIDTH;
      const totalInfillSpace = gapMm - totalR50Width;
      infillWidth = Math.round(totalInfillSpace / numInfills);
    }
    
    // Final calculation
    const totalR50Width = numInfills * R50_WIDTH;
    infillWidth = Math.round((gapMm - totalR50Width) / numInfills);
    
    return {
      r50Count: numInfills,
      infillCount: numInfills,
      uprights: numInfills * UPRIGHTS_PER_R50,
      infillWidth: infillWidth
    };
  };

  const toggleBank = (id) => {
    setBanks(banks.map(b => 
      b.id === id ? { ...b, isOpen: !b.isOpen } : b
    ));
  };

  const handleCalculate = async () => {
    setError("");
    
    // Validate all banks
    const banksData = [];
    for (let i = 0; i < banks.length; i++) {
      const bank = banks[i];
      const total = parseInt(bank.totalLanes);
      const dda = parseInt(bank.ddaLanes);

      if (isNaN(total) || total < 1) {
        setError(`Bank ${i + 1}: Total lanes must be at least 1`);
        return;
      }

      if (isNaN(dda) || dda < 0) {
        setError(`Bank ${i + 1}: DDA lanes must be 0 or greater`);
        return;
      }

      if (dda > total) {
        setError(`Bank ${i + 1}: DDA lanes cannot exceed total lanes`);
        return;
      }

      // Validate high wings selection
      if (!bank.highWings) {
        setError(`Bank ${i + 1}: Please select whether you require high wings`);
        return;
      }

      // Wing height auto-set to 1.8m when high wings selected (only option now)
      if (bank.highWings === "yes" && !bank.wingHeight) {
        setError(`Bank ${i + 1}: Please select wing height`);
        return;
      }

      // Validate bases selection
      if (!bank.requireBases) {
        setError(`Bank ${i + 1}: Please select whether you require bases`);
        return;
      }

      // Validate balustrade selection
      if (!bank.requireBalustrade) {
        setError(`Bank ${i + 1}: Please select whether you require balustrade`);
        return;
      }

      // NOTE: Balustrade validation is now handled later in the balustrade configuration block
      // which properly checks for auto vs manual mode

      // Validate extended warranty selection
      if (!bank.extendedWarranty) {
        setError(`Bank ${i + 1}: Please select whether you want extended warranty`);
        return;
      }

      // Validate warranty years when extended warranty is selected
      if (bank.extendedWarranty === "yes" && !bank.warrantyYears) {
        setError(`Bank ${i + 1}: Please select warranty duration (4 or 5 years)`);
        return;
      }

      // Validate access control selection
      if (!bank.requireAccessControl) {
        setError(`Bank ${i + 1}: Please select whether you want ZKTeco Access Control`);
        return;
      }

      // Validate credential type when access control is required
      if (bank.requireAccessControl === "yes" && !bank.credentialType) {
        setError(`Bank ${i + 1}: Please select a credential type for access control`);
        return;
      }

      // Validate balustrade configuration
      if (bank.requireBalustrade === "yes") {
        if (!bank.balustradeMode) {
          setError(`Bank ${i + 1}: Please select a balustrade configuration mode (Automatic or Manual)`);
          return;
        }
        if (bank.balustradeMode === "auto" && (!bank.totalSpaceWidth || parseInt(bank.totalSpaceWidth) <= 0)) {
          setError(`Bank ${i + 1}: Please enter the total space width for automatic balustrade calculation`);
          return;
        }
        if (bank.balustradeMode === "manual") {
          if (!bank.manualUprights || parseInt(bank.manualUprights) < 2) {
            setError(`Bank ${i + 1}: Please enter at least 2 uprights for manual balustrade`);
            return;
          }
          if (bank.manualInfills.length === 0 || bank.manualInfills.some(inf => !inf.width || parseInt(inf.width) < 0 || parseInt(inf.width) > 1000 || !inf.qty || parseInt(inf.qty) < 1)) {
            setError(`Bank ${i + 1}: Please enter valid infill quantities (min 1) and widths (0-1000mm) for manual balustrade`);
            return;
          }
        }
      }

      // Build manual infills array for API - now includes qty and width
      const manualInfillsData = bank.balustradeMode === "manual" 
        ? bank.manualInfills.map(inf => ({ qty: parseInt(inf.qty) || 1, width: parseInt(inf.width) || 0 }))
        : [];

      banksData.push({
        bank_number: i + 1,
        total_lanes: total,
        dda_lanes: dda,
        high_wings: bank.highWings === "yes",
        wing_height: bank.highWings === "yes" ? bank.wingHeight : null,
        require_bases: bank.requireBases === "yes",
        require_access_control: bank.requireAccessControl === "yes",
        credential_type: bank.requireAccessControl === "yes" ? bank.credentialType : null,
        extended_warranty: bank.extendedWarranty === "yes" ? bank.warrantyYears : null,
        require_balustrade: bank.requireBalustrade === "yes",
        balustrade_mode: bank.requireBalustrade === "yes" ? bank.balustradeMode : null,
        total_space_width: bank.balustradeMode === "auto" ? parseInt(bank.totalSpaceWidth) || 0 : 0,
        manual_uprights: bank.balustradeMode === "manual" ? parseInt(bank.manualUprights) || 0 : 0,
        manual_infills: manualInfillsData
      });
    }

    // Validate project name
    if (!projectName.trim()) {
      setError("Project name is required");
      return;
    }
    
    // Validate quote target for ZKTeco staff
    if (isAdmin && !quoteTarget) {
      setError("Please select who this quote is for (Distributor, Installer, or End User)");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/configure`, {
        banks: banksData,
        project_name: projectName,
        quote_reference: quoteReference || null,
        price_modifier: parseFloat(priceModifier) || 0.0,
        config_id: currentConfigId
      }, getAuthHeaders());
      setResult(response.data);
      // Save the configuration ID so we can use it for PDF download
      if (response.data.configuration_id) {
        setCurrentConfigId(response.data.configuration_id);
      }
      setSuccess("Project saved successfully!");
      setTimeout(() => setSuccess(""), 3000);

      // Check if installer price >= £20,000 for project support (only show once per quote)
      const installerPrice = response.data.final_installer_price || 0;
      if (installerPrice >= 20000 && !supportDeclined) {
        setShowProjectSupport(true);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || "Error calculating configuration";
      setError(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    const originalTitle = document.title;
    document.title = projectName ? `Quote - ${projectName}` : 'ZKTeco Quote';
    window.print();
    setTimeout(() => {
      document.title = originalTitle;
    }, 100);
  };

  const handleDownloadPDF = async () => {
    console.log('Download PDF clicked');
    console.log('currentConfigId:', currentConfigId);
    console.log('result?.configuration_id:', result?.configuration_id);
    
    const configId = currentConfigId || result?.configuration_id;
    
    if (!configId) {
      console.error('No config ID found');
      setError('Please calculate the configuration first.');
      return;
    }

    console.log('Using configId:', configId);

    try {
      setError("");
      console.log('Fetching PDF from:', `${API}/configurations/${configId}/download-pdf`);
      
      const response = await axios.get(
        `${API}/configurations/${configId}/download-pdf`,
        {
          headers: getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      console.log('PDF received, size:', response.data.size);
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Quote_${projectName.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      console.log('PDF download triggered');
      setSuccess('PDF downloaded successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error downloading PDF:', err);
      console.error('Error response:', err.response);
      setError('Failed to download PDF: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleExportCSV = () => {
    if (!result) return;

    let csv = "Category,SKU,Description,Quantity,Unit Price (GBP),Line Total (GBP),Datasheet URL\n";
    result.combined_bom.forEach(item => {
      csv += `${item.category},${item.sku},"${item.description}",${item.quantity},${item.unit_price.toFixed(2)},${item.line_total.toFixed(2)},${item.datasheet_url || ''}\n`;
    });
    csv += `\nTrade Subtotal,,,,,${result.trade_subtotal.toFixed(2)}`;

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `comet-speedlane-bom-${Date.now()}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadDatasheet = (url, sku) => {
    if (!url || url.includes('yourwebsite.com')) {
      alert('Datasheet URL not yet configured. Please update the pricing.csv file with your website URLs.');
      return;
    }
    // Open in new window with noopener and noreferrer to avoid ad blocker issues
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const loadSavedConfigurations = async () => {
    try {
      const response = await axios.get(`${API}/configurations`, getAuthHeaders());
      setSavedConfigs(response.data);
    } catch (err) {
      console.error('Error loading saved configurations:', err);
    }
  };

  const handleSaveConfiguration = async () => {
    if (!projectName.trim()) {
      setError("Please enter a project name");
      return;
    }

    setError("");
    setSuccess("");

    const banksData = banks.map((bank, i) => ({
      bank_number: i + 1,
      total_lanes: parseInt(bank.totalLanes) || 0,
      dda_lanes: parseInt(bank.ddaLanes) || 0,
      high_wings: bank.highWings === "yes",
      wing_height: bank.highWings === "yes" ? bank.wingHeight : null
    }));

    try {
      if (currentConfigId) {
        await axios.put(`${API}/configurations/${currentConfigId}`, {
          project_name: projectName,
          banks: banksData,
          sundry_items: sundryItems,
          final_total: result?.final_total || 0
        }, getAuthHeaders());
        setSuccess("Configuration updated successfully!");
      } else {
        const response = await axios.post(`${API}/configurations/save`, {
          project_name: projectName,
          banks: banksData,
          sundry_items: sundryItems,
          final_total: result?.final_total || 0
        }, getAuthHeaders());
        setCurrentConfigId(response.data.id);
        setSuccess("Configuration saved successfully!");
      }
      loadSavedConfigurations();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || "Error saving configuration");
    }
  };

  const handleLoadConfiguration = (config) => {
    setProjectName(config.project_name || "");
    setQuoteReference(config.quote_reference || "");
    setCurrentConfigId(config.id);
    
    // Load sundry items if present
    setSundryItems(config.sundry_items || []);
    
    // Ensure banks is an array before mapping
    const banksArray = Array.isArray(config.banks) ? config.banks : [];
    const loadedBanks = banksArray.map((bank, index) => ({
      id: index + 1,
      totalLanes: bank.total_lanes ? bank.total_lanes.toString() : "",
      ddaLanes: bank.dda_lanes ? bank.dda_lanes.toString() : "",
      highWings: bank.high_wings ? "yes" : "no",
      wingHeight: bank.wing_height || "",
      isOpen: true
    }));
    
    // If no banks loaded, set default empty bank
    setBanks(loadedBanks.length > 0 ? loadedBanks : [{
      id: 1,
      totalLanes: "",
      ddaLanes: "",
      highWings: "no",
      wingHeight: "",
      isOpen: true
    }]);
    setShowSaved(false);
    setResult(null);
    setSuccess("Configuration loaded!");
    setTimeout(() => setSuccess(""), 3000);
  };

  const handleDeleteConfiguration = async (configId) => {
    if (!window.confirm("Are you sure you want to delete this configuration?")) return;
    
    try {
      await axios.delete(`${API}/configurations/${configId}`, getAuthHeaders());
      setSuccess("Configuration deleted!");
      loadSavedConfigurations();
      if (currentConfigId === configId) {
        setCurrentConfigId(null);
        setProjectName("");
      }
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError("Error deleting configuration");
    }
  };

  const handleNewConfiguration = () => {
    setProjectName("");
    setCurrentConfigId(null);
    setBanks([{
      id: 1,
      totalLanes: "",
      ddaLanes: "",
      highWings: "no",
      wingHeight: "",
      isOpen: true
    }]);
    setResult(null);
    setShowSaved(false);
  };

  const handleUpdateStatus = async () => {
    if (!selectedConfig || !newStatus) return;
    
    try {
      await axios.put(
        `${API}/configurations/${selectedConfig.id}/status`,
        {
          status: newStatus,
          lost_reason: newStatus === 'lost' ? lostReason : null
        },
        getAuthHeaders()
      );
      
      setSuccess(`Project marked as ${newStatus}!`);
      setStatusDialogOpen(false);
      setSelectedConfig(null);
      setNewStatus('');
      setLostReason('');
      loadSavedConfigurations();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || "Error updating project status");
      setTimeout(() => setError(""), 5000);
    }
  };

  const openStatusDialog = (config, status) => {
    setSelectedConfig(config);
    setNewStatus(status);
    setLostReason('');
    setStatusDialogOpen(true);
  };

  useEffect(() => {
    fetchUserRole();
    loadSavedConfigurations();
    
    // Load selected quote if provided
    if (selectedQuote) {
      handleLoadConfiguration(selectedQuote);
    }
  }, [selectedQuote]);

  const fetchUserRole = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, getAuthHeaders());
      setUserRole(response.data.role);
    } catch (err) {
      console.error('Error fetching user role:', err);
    }
  };

  // BOM pricing visibility based on quoteTarget for admin users
  const isAdmin = userRole === 'admin' || userRole === 'zkteco_admin' || userRole === 'zkteco';
  const isDistributorUser = userRole === 'distributor' || userRole === 'distributor_admin';
  const showBomDistributorPrice = !installerOnlyCapture && ((isAdmin && quoteTarget === 'distributor') || isDistributorUser);
  const showBomInstallerPrice = isAdmin ? (quoteTarget === 'distributor' || quoteTarget === 'installer') : (isDistributorUser || userRole === 'installer');
  const showBomEndUserPrice = isAdmin && quoteTarget === 'end_user';

  return (
    <><div className="bg-slate-50">
      <div className="grid lg:grid-cols-3 gap-6">
          {/* Input Section */}
          <div className="lg:col-span-1 print:hidden space-y-4">
            {/* Back to Dashboard Button */}
            {onBack && (
              <Button
                variant="outline"
                onClick={onBack}
                className="w-full mb-4"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                {backLabel}
              </Button>
            )}
            
            {/* Action Buttons */}
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={handleNewQuote}
                className="flex-1"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Quote
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setShowSaved(!showSaved)}
                className="flex-1"
              >
                <FolderOpen className="w-4 h-4 mr-2" />
                {showSaved ? 'Hide' : 'Saved'}
              </Button>
            </div>

            {/* Saved Configurations List */}
            {showSaved && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Saved Projects</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {!savedConfigs || savedConfigs.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-4">No saved projects yet</p>
                  ) : (
                    savedConfigs.map((config) => (
                      <div key={config.id} className="border rounded hover:bg-slate-50">
                        <div className="flex items-center justify-between p-2">
                          <button
                            onClick={() => handleLoadConfiguration(config)}
                            className="flex-1 text-left"
                          >
                            <div className="flex items-center gap-2">
                              <div className="text-sm font-medium text-slate-900">{config.project_name || 'Untitled'}</div>
                              {config.status === 'won' && (
                                <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Won</Badge>
                              )}
                              {config.status === 'lost' && (
                                <Badge className="bg-red-100 text-red-800 hover:bg-red-100">Lost</Badge>
                              )}
                              {config.status === 'active' && (
                                <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">Active</Badge>
                              )}
                            </div>
                            <div className="text-xs text-slate-500">
                              {new Date(config.updated_at).toLocaleDateString('en-GB')} - {(config.banks || []).length} bank(s)
                              {config.final_total && ` - £${config.final_total.toFixed(2)}`}
                            </div>
                          </button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteConfiguration(config.id)}
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        <div className="px-2 pb-2 pt-0 space-y-1">
                          {config.pdf_path && (
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={downloadingPdf === config.id}
                              onClick={async () => {
                                setDownloadingPdf(config.id);
                                setError("");
                                try {
                                  console.log(`Downloading PDF for config ${config.id}`);
                                  const response = await axios.get(
                                    `${API}/configurations/${config.id}/download-pdf`,
                                    {
                                      ...getAuthHeaders(),
                                      responseType: 'blob'
                                    }
                                  );
                                  
                                  console.log('PDF response received, size:', response.data.size);
                                  
                                  // Create download link
                                  const url = window.URL.createObjectURL(new Blob([response.data]));
                                  const link = document.createElement('a');
                                  link.href = url;
                                  const filename = `Quote_${config.project_name.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
                                  link.setAttribute('download', filename);
                                  document.body.appendChild(link);
                                  link.click();
                                  link.remove();
                                  window.URL.revokeObjectURL(url);
                                  
                                  console.log('PDF download triggered successfully');
                                  setSuccess('PDF downloaded successfully!');
                                  setTimeout(() => setSuccess(""), 3000);
                                } catch (err) {
                                  console.error('Error downloading PDF:', err);
                                  const errorMsg = err.response?.data?.detail || err.message || 'Failed to download PDF quote';
                                  setError(errorMsg);
                                  setTimeout(() => setError(""), 5000);
                                } finally {
                                  setDownloadingPdf(null);
                                }
                              }}
                              className="w-full text-xs h-7 text-blue-600 hover:text-blue-700 border-blue-200 hover:bg-blue-50"
                            >
                              <Download className="w-3 h-3 mr-1" />
                              {downloadingPdf === config.id ? 'Downloading...' : 'Download Quote PDF'}
                            </Button>
                          )}
                          
                          {config.status === 'active' && (
                            <div className="flex gap-1">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openStatusDialog(config, 'won')}
                                className="flex-1 text-xs h-7 text-green-700 hover:text-green-800 border-green-300 hover:bg-green-50"
                              >
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Won
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openStatusDialog(config, 'lost')}
                                className="flex-1 text-xs h-7 text-red-700 hover:text-red-800 border-red-300 hover:bg-red-50"
                              >
                                <XCircle className="w-3 h-3 mr-1" />
                                Lost
                              </Button>
                            </div>
                          )}
                          
                          {(config.status === 'won' || config.status === 'lost') && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openStatusDialog(config, 'active')}
                              className="w-full text-xs h-7 text-slate-600 hover:text-slate-700 border-slate-300 hover:bg-slate-50"
                            >
                              Reopen as Active
                            </Button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>
            )}

            {/* Project Name & Price Modifier */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Project Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="project-name">Project Name <span className="text-red-500">*</span></Label>
                    <Input
                      id="project-name"
                      data-testid="project-name-input"
                      type="text"
                      value={projectName}
                      onChange={(e) => setProjectName(e.target.value)}
                      placeholder="e.g., Main Entrance Speedlanes"
                      required
                    />
                    {currentConfigId && (
                      <p className="text-xs text-green-600">✓ Loaded from saved project</p>
                    )}
                    {!currentConfigId && projectName && (
                      <p className="text-xs text-blue-600">Auto-saves after calculation</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="quote-reference">Quote Reference (Optional)</Label>
                    <Input
                      id="quote-reference"
                      data-testid="quote-reference-input"

                      type="text"
                      value={quoteReference}
                      onChange={(e) => setQuoteReference(e.target.value)}
                      placeholder="e.g., Q-2024-001"
                    />
                  </div>
                  
                  {(userRole === 'distributor' || userRole === 'admin') && (
                    <div className="space-y-2">
                      <Label htmlFor="price-modifier">Installer Discount (%)</Label>
                      <Input
                        id="price-modifier"
                        data-testid="price-modifier-input"
                        type="number"
                        step="0.1"
                        min="0"
                        value={priceModifier}
                        onChange={(e) => setPriceModifier(e.target.value)}
                        placeholder="e.g., 5 for 5% discount"
                      />
                      <p className="text-xs text-slate-500">
                        Enter discount percentage to reduce installer price (e.g., 5 for 5% off)
                      </p>
                    </div>
                  )}
                  
                  {/* Quote Target Selector - ZKTeco Staff Only */}
                  <QuoteTargetSelector 
                    quoteTarget={quoteTarget} 
                    setQuoteTarget={setQuoteTarget} 
                    userRole={userRole} 
                  />
                </div>
              </CardContent>
            </Card>

            {/* Banks Configuration */}
            {banks.map((bank, index) => (
              <Collapsible key={bank.id} open={bank.isOpen} onOpenChange={() => toggleBank(bank.id)}>
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" size="sm" className="p-0 h-6 w-6">
                            {bank.isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          </Button>
                        </CollapsibleTrigger>
                        <CardTitle className="text-base">Bank {index + 1}</CardTitle>
                      </div>
                      {banks.length > 1 && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => removeBank(bank.id)}
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`remove-bank-${index}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CollapsibleContent>
                    <CardContent className="space-y-4 pt-0">
                      <div className="space-y-2">
                        <Label htmlFor={`total-lanes-${bank.id}`}>Total Number of Lanes in this bank (includes the DDA Lane below)</Label>
                        <Input
                          id={`total-lanes-${bank.id}`}
                          data-testid={`total-lanes-input-${index}`}
                          type="number"
                          min="1"
                          value={bank.totalLanes}
                          onChange={(e) => updateBank(bank.id, 'totalLanes', e.target.value)}
                          placeholder="e.g., 4"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor={`dda-lanes-${bank.id}`}>Number of DDA Lanes in this bank</Label>
                        <Input
                          id={`dda-lanes-${bank.id}`}
                          data-testid={`dda-lanes-input-${index}`}
                          type="number"
                          min="0"
                          value={bank.ddaLanes}
                          onChange={(e) => updateBank(bank.id, 'ddaLanes', e.target.value)}
                          placeholder="e.g., 1"
                        />
                      </div>

                      <div className="space-y-3">
                        <Label>High wings required? <span className="text-red-500">*</span></Label>
                        <RadioGroup 
                          value={bank.highWings} 
                          onValueChange={(value) => updateBank(bank.id, 'highWings', value)}
                          data-testid={`high-wings-radio-${index}`}
                          className={!bank.highWings ? "border border-dashed border-slate-300 rounded-md p-2" : ""}
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="yes" id={`high-wings-yes-${bank.id}`} />
                            <Label htmlFor={`high-wings-yes-${bank.id}`} className="font-normal cursor-pointer">Yes</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="no" id={`high-wings-no-${bank.id}`} />
                            <Label htmlFor={`high-wings-no-${bank.id}`} className="font-normal cursor-pointer">No</Label>
                          </div>
                        </RadioGroup>
                      </div>

                      {bank.highWings === "yes" && (
                        <div className="space-y-3 pl-4 border-l-2 border-slate-200">
                          <Label>Wing Height</Label>
                          <RadioGroup 
                            value={bank.wingHeight} 
                            onValueChange={(value) => updateBank(bank.id, 'wingHeight', value)}
                            data-testid={`wing-height-radio-${index}`}
                          >
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="1.8m" id={`wing-height-1.8-${bank.id}`} />
                              <Label htmlFor={`wing-height-1.8-${bank.id}`} className="font-normal cursor-pointer">1.8m High Wings</Label>
                            </div>
                          </RadioGroup>
                          <p className="text-xs text-slate-500">1.8m extra high wings for maximum security</p>
                        </div>
                      )}

                      <div className="space-y-3">
                        <Label>Do you require bases? <span className="text-red-500">*</span></Label>
                        <p className="text-xs text-slate-500 -mt-1">
                          Bases are designed specifically for the Comet and come with cable runs for ease of installation
                        </p>
                        <RadioGroup 
                          value={bank.requireBases} 
                          onValueChange={(value) => updateBank(bank.id, 'requireBases', value)}
                          data-testid={`require-bases-radio-${index}`}
                          className={!bank.requireBases ? "border border-dashed border-slate-300 rounded-md p-2" : ""}
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="yes" id={`require-bases-yes-${bank.id}`} />
                            <Label htmlFor={`require-bases-yes-${bank.id}`} className="font-normal cursor-pointer">Yes</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="no" id={`require-bases-no-${bank.id}`} />
                            <Label htmlFor={`require-bases-no-${bank.id}`} className="font-normal cursor-pointer">No</Label>
                          </div>
                        </RadioGroup>
                      </div>

                      {/* Balustrade Question */}
                      <div className="space-y-3 pt-4 border-t border-slate-200">
                        <Label>Do you require balustrade (R50)? <span className="text-red-500">*</span></Label>
                        <p className="text-xs text-slate-500 -mt-1">
                          R50 balustrade fills gaps between the speedlane bank and walls/obstacles
                        </p>
                        <RadioGroup 
                          value={bank.requireBalustrade} 
                          onValueChange={(value) => updateBank(bank.id, 'requireBalustrade', value)}
                          data-testid={`require-balustrade-radio-${index}`}
                          className={!bank.requireBalustrade ? "border border-dashed border-slate-300 rounded-md p-2" : ""}
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="yes" id={`require-balustrade-yes-${bank.id}`} />
                            <Label htmlFor={`require-balustrade-yes-${bank.id}`} className="font-normal cursor-pointer">Yes</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="no" id={`require-balustrade-no-${bank.id}`} />
                            <Label htmlFor={`require-balustrade-no-${bank.id}`} className="font-normal cursor-pointer">No</Label>
                          </div>
                        </RadioGroup>
                      </div>

                      {/* Total Space Width - shown when balustrade is "yes" */}
                      {bank.requireBalustrade === "yes" && (
                        <div className="space-y-4 pl-4 border-l-2 border-purple-300 bg-purple-50 p-3 rounded-r-md">
                          <div>
                            <Label>Balustrade Configuration Mode <span className="text-red-500">*</span></Label>
                            <p className="text-xs text-slate-600 mb-2">
                              Choose automatic calculation or manual specification
                            </p>
                            <RadioGroup 
                              value={bank.balustradeMode} 
                              onValueChange={(value) => updateBank(bank.id, 'balustradeMode', value)}
                              className="flex gap-4"
                            >
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem value="auto" id={`balustrade-auto-${bank.id}`} />
                                <Label htmlFor={`balustrade-auto-${bank.id}`} className="font-normal cursor-pointer">
                                  Automatic (enter space width)
                                </Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem value="manual" id={`balustrade-manual-${bank.id}`} />
                                <Label htmlFor={`balustrade-manual-${bank.id}`} className="font-normal cursor-pointer">
                                  Manual (specify uprights & infills)
                                </Label>
                              </div>
                            </RadioGroup>
                          </div>

                          {/* Automatic Mode */}
                          {bank.balustradeMode === "auto" && (
                            <div className="space-y-2 p-3 bg-white rounded border border-purple-200">
                              <Label>Total available space width (mm) <span className="text-red-500">*</span></Label>
                              <p className="text-xs text-slate-600 -mt-1">
                                The system will calculate the gap and required R50 frames + infill panels automatically.
                              </p>
                              <Input
                                type="number"
                                min="0"
                                step="1"
                                placeholder="e.g., 4000"
                                value={bank.totalSpaceWidth}
                                onChange={(e) => updateBank(bank.id, 'totalSpaceWidth', e.target.value)}
                                data-testid={`total-space-width-${index}`}
                                className="max-w-xs"
                              />
                            </div>
                          )}

                          {/* Manual Mode */}
                          {bank.balustradeMode === "manual" && (
                            <div className="space-y-4 p-3 bg-white rounded border border-purple-200">
                              {/* Uprights */}
                              <div className="space-y-2">
                                <Label>Number of Uprights Required <span className="text-red-500">*</span></Label>
                                <p className="text-xs text-slate-600 -mt-1">
                                  R50 frames come with 2 uprights each. Odd numbers will round up (e.g., 3 uprights = 2x R50 frames).
                                </p>
                                <Input
                                  type="number"
                                  min="2"
                                  step="1"
                                  placeholder="e.g., 4"
                                  value={bank.manualUprights}
                                  onChange={(e) => updateBank(bank.id, 'manualUprights', e.target.value)}
                                  data-testid={`manual-uprights-${index}`}
                                  className="max-w-xs"
                                />
                                {bank.manualUprights && parseInt(bank.manualUprights) >= 2 && (
                                  <p className="text-xs text-purple-700">
                                    = {Math.ceil(parseInt(bank.manualUprights) / 2)}x ACC-TSA-R50 frame(s)
                                  </p>
                                )}
                              </div>

                              {/* Manual Infills */}
                              <div className="space-y-2">
                                <Label>Infill Panels</Label>
                                <p className="text-xs text-slate-600 -mt-1">
                                  Specify quantity and width for each infill panel size (0-1000mm). These will be marked as &quot;manual size&quot; in the quote.
                                </p>
                                
                                {bank.manualInfills.map((infill, infillIndex) => (
                                  <div key={infill.id} className="flex items-center gap-2 flex-wrap">
                                    <span className="text-sm text-slate-500 w-16">Infill {infillIndex + 1}:</span>
                                    <Input
                                      type="number"
                                      min="1"
                                      step="1"
                                      placeholder="Qty"
                                      value={infill.qty}
                                      onChange={(e) => updateManualInfill(bank.id, infill.id, 'qty', e.target.value)}
                                      className="w-16"
                                    />
                                    <span className="text-sm text-slate-500">x</span>
                                    <Input
                                      type="number"
                                      min="0"
                                      max="1000"
                                      step="1"
                                      placeholder="Width (mm)"
                                      value={infill.width}
                                      onChange={(e) => updateManualInfill(bank.id, infill.id, 'width', e.target.value)}
                                      className="w-24"
                                    />
                                    <span className="text-sm text-slate-500">mm</span>
                                    {bank.manualInfills.length > 1 && (
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => removeManualInfill(bank.id, infill.id)}
                                        className="text-red-500 hover:text-red-700 p-1 h-auto"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    )}
                                  </div>
                                ))}
                                
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => addManualInfill(bank.id)}
                                  className="mt-2"
                                >
                                  <Plus className="h-4 w-4 mr-1" /> Add Another Infill
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Extended Warranty Question */}
                      <div className="space-y-3 pt-4 border-t border-slate-200">
                        <Label>Do you want to extend the 3 Year Standard warranty? <span className="text-red-500">*</span></Label>
                        <p className="text-xs text-slate-500 -mt-1">
                          Extend your warranty coverage beyond the standard 3 years
                        </p>
                        <RadioGroup 
                          value={bank.extendedWarranty} 
                          onValueChange={(value) => updateBank(bank.id, 'extendedWarranty', value)}
                          data-testid={`extended-warranty-radio-${index}`}
                          className={!bank.extendedWarranty ? "border border-dashed border-slate-300 rounded-md p-2" : ""}
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="yes" id={`extended-warranty-yes-${bank.id}`} />
                            <Label htmlFor={`extended-warranty-yes-${bank.id}`} className="font-normal cursor-pointer">Yes</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="no" id={`extended-warranty-no-${bank.id}`} />
                            <Label htmlFor={`extended-warranty-no-${bank.id}`} className="font-normal cursor-pointer">No</Label>
                          </div>
                        </RadioGroup>
                      </div>

                      {/* Warranty Years Selection - shown when extended warranty is "yes" */}
                      {bank.extendedWarranty === "yes" && (
                        <div className="space-y-3 pl-4 border-l-2 border-amber-300 bg-amber-50 p-3 rounded-r-md">
                          <Label>Select warranty duration <span className="text-red-500">*</span></Label>
                          <RadioGroup 
                            value={bank.warrantyYears} 
                            onValueChange={(value) => updateBank(bank.id, 'warrantyYears', value)}
                            data-testid={`warranty-years-radio-${index}`}
                          >
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="4_years" id={`warranty-4-years-${bank.id}`} />
                              <Label htmlFor={`warranty-4-years-${bank.id}`} className="font-normal cursor-pointer">4 Years Warranty (+1 year)</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="5_years" id={`warranty-5-years-${bank.id}`} />
                              <Label htmlFor={`warranty-5-years-${bank.id}`} className="font-normal cursor-pointer">5 Years Warranty (+2 years)</Label>
                            </div>
                          </RadioGroup>
                        </div>
                      )}

                      {/* Access Control Question */}
                      <div className="space-y-3 pt-4 border-t border-slate-200">
                        <Label>Do you want ZKTeco Access Control on these speedlanes? <span className="text-red-500">*</span></Label>
                        <p className="text-xs text-slate-500 -mt-1">
                          Add readers and controllers for entry/exit control
                        </p>
                        <RadioGroup 
                          value={bank.requireAccessControl} 
                          onValueChange={(value) => updateBank(bank.id, 'requireAccessControl', value)}
                          data-testid={`require-access-control-radio-${index}`}
                          className={!bank.requireAccessControl ? "border border-dashed border-slate-300 rounded-md p-2" : ""}
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="yes" id={`require-ac-yes-${bank.id}`} />
                            <Label htmlFor={`require-ac-yes-${bank.id}`} className="font-normal cursor-pointer">Yes</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="no" id={`require-ac-no-${bank.id}`} />
                            <Label htmlFor={`require-ac-no-${bank.id}`} className="font-normal cursor-pointer">No</Label>
                          </div>
                        </RadioGroup>
                      </div>

                      {/* Credential Type Selection - shown when access control is "yes" */}
                      {bank.requireAccessControl === "yes" && (
                        <div className="space-y-3 pt-4 border-t border-slate-200 bg-blue-50 p-4 rounded-md -mx-4">
                          <Label>Select Credential Type <span className="text-red-500">*</span></Label>
                          <p className="text-xs text-slate-500 -mt-1">
                            Choose the type of reader technology for access control
                          </p>
                          <RadioGroup 
                            value={bank.credentialType} 
                            onValueChange={(value) => updateBank(bank.id, 'credentialType', value)}
                            data-testid={`credential-type-radio-${index}`}
                            className={!bank.credentialType ? "border border-dashed border-blue-300 rounded-md p-2 bg-white" : "bg-white rounded-md p-2"}
                          >
                            <div className="space-y-2">
                              <div className="flex items-start space-x-2">
                                <RadioGroupItem value="rfid" id={`cred-rfid-${bank.id}`} className="mt-1" />
                                <div>
                                  <Label htmlFor={`cred-rfid-${bank.id}`} className="font-normal cursor-pointer">RFID Card</Label>
                                  <p className="text-xs text-slate-500">Mifare card readers with Atlas controllers</p>
                                </div>
                              </div>
                              <div className="flex items-start space-x-2">
                                <RadioGroupItem value="qr_code" id={`cred-qr-${bank.id}`} className="mt-1" />
                                <div>
                                  <Label htmlFor={`cred-qr-${bank.id}`} className="font-normal cursor-pointer">QR Code</Label>
                                  <p className="text-xs text-slate-500">QR code readers with Atlas controllers</p>
                                </div>
                              </div>
                              <div className="flex items-start space-x-2">
                                <RadioGroupItem value="face" id={`cred-face-${bank.id}`} className="mt-1" />
                                <div>
                                  <Label htmlFor={`cred-face-${bank.id}`} className="font-normal cursor-pointer">Face Recognition</Label>
                                  <p className="text-xs text-slate-500">Facial recognition with Bio CVSecurity system</p>
                                </div>
                              </div>
                              <div className="flex items-start space-x-2">
                                <RadioGroupItem value="palm" id={`cred-palm-${bank.id}`} className="mt-1" />
                                <div>
                                  <Label htmlFor={`cred-palm-${bank.id}`} className="font-normal cursor-pointer">Palm Recognition</Label>
                                  <p className="text-xs text-slate-500">Palm vein recognition with Bio CVSecurity system</p>
                                </div>
                              </div>
                            </div>
                          </RadioGroup>
                        </div>
                      )}

                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            ))}

            {/* Add Bank Button */}
            <Button 
              variant="outline" 
              onClick={addBank}
              className="w-full"
              data-testid="add-bank-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Another Bank
            </Button>

            {/* Error Display */}
            {error && (
              <Alert variant="destructive" data-testid="error-alert">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Success Display */}
            {success && (
              <Alert className="bg-green-50 border-green-200" data-testid="success-alert">
                <AlertDescription className="text-green-800">{success}</AlertDescription>
              </Alert>
            )}

            {/* Action Buttons */}
            <Button 
              data-testid="calculate-button"
              onClick={handleCalculate} 
              className="w-full"
              disabled={loading}
            >
              {loading ? "Calculating..." : "Calculate & Save"}
            </Button>
          </div>

          {/* Results Section */}
          <div className="lg:col-span-2">
            {result && (
              <div className="space-y-6">
                {/* Action Buttons */}
                <div className="flex gap-2 print:hidden" data-testid="action-buttons">
                  <Button 
                    data-testid="export-csv-button"
                    onClick={handleExportCSV} 
                    variant="outline"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Export CSV
                  </Button>
                  <Button 
                    data-testid="add-sundry-button"
                    onClick={handleAddSundryItem} 
                    variant="outline"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Sundry Line
                  </Button>
                </div>

                {/* Base pricing (POA) call-to-action - only when quote includes bases */}
                {(result.combined_bom || []).some(it => it.poa) && (
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 print:hidden" data-testid="base-poa-banner">
                    <div className="flex items-start gap-2">
                      <Mail className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-sm font-semibold text-blue-900">Your quote includes bases (price on application)</p>
                        <p className="text-xs text-blue-700">Base pricing isn't shown on-screen. Email ZKTeco to request a quote for the bases below.</p>
                      </div>
                    </div>
                    <Button
                      data-testid="email-base-pricing-button"
                      className="bg-blue-600 hover:bg-blue-700 text-white whitespace-nowrap"
                      onClick={() => {
                        const bases = (result.combined_bom || []).filter(it => it.poa);
                        const lines = bases.map(b => `- ${b.sku || ''} (${b.description || ''}) x ${b.quantity || 1}`).join('\n');
                        const subject = `Base pricing request${projectName ? ' - ' + projectName : ''}`;
                        const body = `Hi ZKTeco,\n\nPlease provide base pricing for the following items on our quote${projectName ? ' (' + projectName + ')' : ''}:\n\n${lines}\n\nThank you.`;
                        window.location.href = `mailto:${BASE_PRICING_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
                      }}
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Email ZKTeco for base pricing
                    </Button>
                  </div>
                )}

                {/* Per-Bank Results - Hidden from print */}
                <div className="print:hidden">
                {(result.banks || []).map((bank, index) => (
                  <Card key={index} data-testid={`bank-result-${index}`}>
                    <CardHeader className="bg-slate-50">
                      <CardTitle className="text-lg">Bank {bank.bank_number} Details</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-6 space-y-4">
                      {/* Lane Composition */}
                      <div>
                        <div className="text-sm font-medium text-slate-700 mb-2">Lane Composition</div>
                        <div className="grid grid-cols-2 gap-3 mb-2">
                          <div className="bg-blue-50 p-3 rounded border border-blue-200">
                            <div className="text-xs text-blue-700">Master (S1000)</div>
                            <div className="text-xl font-semibold text-blue-900">{bank.master_lanes}</div>
                          </div>
                          <div className="bg-slate-50 p-3 rounded">
                            <div className="text-xs text-slate-600">Slave (S1200)</div>
                            <div className="text-xl font-semibold text-slate-900">{bank.slave_lanes}</div>
                          </div>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-3">
                          <div className="bg-slate-50 p-3 rounded">
                            <div className="text-xs text-slate-600">Standard Wings</div>
                            <div className="text-xl font-semibold text-slate-900">{bank.standard_lanes}</div>
                          </div>
                          <div className="bg-slate-50 p-3 rounded">
                            <div className="text-xs text-slate-600">DDA Wide Wings</div>
                            <div className="text-xl font-semibold text-slate-900">{bank.dda_lanes}</div>
                          </div>
                        </div>
                        {bank.high_wings && (
                          <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs">
                            <span className="font-medium text-blue-900">High Wings: {bank.wing_height}</span>
                          </div>
                        )}
                      </div>

                      {/* Physical Width */}
                      <div>
                        <div className="text-sm font-medium text-slate-700 mb-2">Physical Bank Width</div>
                        <div className="grid sm:grid-cols-2 gap-3">
                          <div className="bg-slate-50 p-3 rounded">
                            <div className="text-xs text-slate-600">Millimetres</div>
                            <div className="text-xl font-semibold text-slate-900">{bank.total_width_mm.toLocaleString()} mm</div>
                          </div>
                          <div className="bg-slate-50 p-3 rounded">
                            <div className="text-xs text-slate-600">Metres</div>
                            <div className="text-xl font-semibold text-slate-900">{bank.total_width_m} m</div>
                          </div>
                        </div>
                      </div>

                      {/* Base Requirements */}
                      <div>
                        <div className="text-sm font-medium text-slate-700 mb-2">Base Requirements</div>
                        <div className="grid sm:grid-cols-3 gap-3">
                          <div className="bg-slate-50 p-3 rounded">
                            <div className="text-xs text-slate-600">Standard</div>
                            <div className="text-xl font-semibold text-slate-900">{bank.standard_bases}</div>
                          </div>
                          <div className="bg-slate-50 p-3 rounded">
                            <div className="text-xs text-slate-600">DDA</div>
                            <div className="text-xl font-semibold text-slate-900">{bank.dda_bases}</div>
                          </div>
                          <div className="bg-slate-50 p-3 rounded">
                            <div className="text-xs text-slate-600">Midpoint</div>
                            <div className="text-xl font-semibold text-slate-900">{bank.midpoint_bases}</div>
                          </div>
                        </div>
                      </div>

                      {/* R50 Balustrade Requirements */}
                      {bank.balustrade && (
                        <div>
                          <div className="text-sm font-medium text-slate-700 mb-2">
                            R50 Balustrade 
                            <span className="ml-2 text-xs font-normal text-purple-600">
                              ({bank.balustrade.mode === 'manual' ? 'Manual Configuration' : 'Auto Calculated'})
                            </span>
                          </div>
                          <div className="bg-purple-50 border border-purple-200 p-3 rounded">
                            <div className="grid sm:grid-cols-3 gap-3">
                              {bank.balustrade.gap_mm && (
                                <div>
                                  <div className="text-xs text-purple-600">Gap to Fill</div>
                                  <div className="text-lg font-semibold text-purple-900">{bank.balustrade.gap_mm} mm</div>
                                </div>
                              )}
                              <div>
                                <div className="text-xs text-purple-600">R50 Frames</div>
                                <div className="text-lg font-semibold text-purple-900">{bank.balustrade.r50_count}x</div>
                              </div>
                              <div>
                                <div className="text-xs text-purple-600">Uprights</div>
                                <div className="text-lg font-semibold text-purple-900">{bank.balustrade.uprights}x</div>
                              </div>
                            </div>
                            
                            {/* Infill widths */}
                            <div className="mt-3 pt-3 border-t border-purple-200">
                              <div className="text-xs text-purple-600 mb-2">
                                Infill Panels ({bank.balustrade.infill_count}x)
                                {bank.balustrade.mode === 'manual' && <span className="ml-1 text-purple-500">(manual size)</span>}
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {bank.balustrade.infill_widths?.map((width, idx) => (
                                  <span key={idx} className="px-2 py-1 bg-purple-100 rounded text-sm font-medium text-purple-800">
                                    {width}mm
                                  </span>
                                )) || (
                                  <span className="px-2 py-1 bg-purple-100 rounded text-sm font-medium text-purple-800">
                                    {bank.balustrade.infill_width_mm}mm
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            <p className="text-xs text-purple-700 mt-3">
                              {bank.balustrade.mode === 'manual' 
                                ? 'Manual configuration - infill sizes specified by user.'
                                : 'Auto calculated - speedgate positioned centrally with equal-sized infills.'}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Bank Subtotal */}
                      <div className="pt-2 border-t">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-slate-700">Bank {bank.bank_number} Subtotal:</span>
                          <span className="text-lg font-bold text-slate-900">£{bank.bank_subtotal.toFixed(2)}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                </div>

                {/* Combined BOM */}
                <div ref={quoteBoxRef}>
                <Card data-testid="combined-bom-card">
                  <CardHeader className="bg-slate-900 text-white flex flex-row items-center justify-between">
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <FileText className="w-5 h-5" />
                        Combined Bill of Materials
                      </CardTitle>
                      <CardDescription className="text-slate-300">
                        {result.total_banks} {result.total_banks === 1 ? 'Bank' : 'Banks'} - All items consolidated
                      </CardDescription>
                    </div>
                    {isDistributorUser ? (
                      <div className="flex gap-2" data-html2canvas-ignore="true">
                        <Button
                          variant="outline"
                          size="sm"
                          data-testid="download-distributor-quote-btn"
                          onClick={() => handleDownloadQuoteImage(false)}
                          className="bg-white text-slate-900 hover:bg-slate-100 flex items-center gap-2"
                        >
                          <Download className="h-4 w-4" />
                          Distributor Quote
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          data-testid="download-installer-quote-btn"
                          onClick={() => handleDownloadQuoteImage(true)}
                          className="bg-white text-slate-900 hover:bg-slate-100 flex items-center gap-2"
                        >
                          <Download className="h-4 w-4" />
                          Installer Quote
                        </Button>
                      </div>
                    ) : (
                      <Button 
                        variant="outline" 
                        size="sm"
                        data-html2canvas-ignore="true"
                        onClick={() => handleDownloadQuoteImage(false)}
                        className="bg-white text-slate-900 hover:bg-slate-100 flex items-center gap-2"
                      >
                        <Download className="h-4 w-4" />
                        Download Quote
                      </Button>
                    )}
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="combined-bom-table">
                        <thead>
                          <tr className="border-b border-slate-200">
                            <th className="text-left py-3 px-2 font-semibold text-slate-700">Category</th>
                            <th className="text-left py-3 px-2 font-semibold text-slate-700">SKU</th>
                            <th className="text-left py-3 px-2 font-semibold text-slate-700">Description</th>
                            <th className="text-right py-3 px-2 font-semibold text-slate-700">Qty</th>
                            {showBomDistributorPrice && (
                              <>
                                <th className="text-right py-3 px-2 font-semibold text-purple-700">Distributor Price</th>
                                <th className="text-right py-3 px-2 font-semibold text-purple-700">Dist. Total</th>
                              </>
                            )}
                            {showBomInstallerPrice && (
                              <>
                                <th className="text-right py-3 px-2 font-semibold text-green-700">{userRole === 'installer' ? 'Unit Price' : 'Installer Price'}</th>
                                <th className="text-right py-3 px-2 font-semibold text-green-700">{userRole === 'installer' ? 'Line Total' : 'Inst. Total'}</th>
                              </>
                            )}
                            {showBomEndUserPrice && (
                              <>
                                <th className="text-right py-3 px-2 font-semibold text-orange-700">End User Price</th>
                                <th className="text-right py-3 px-2 font-semibold text-orange-700">EU Total</th>
                              </>
                            )}
                            <th className="text-center py-3 px-2 font-semibold text-slate-700 print:hidden"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {(result.combined_bom || []).map((item, index) => (
                            <tr 
                              key={index} 
                              className="border-b border-slate-100 hover:bg-slate-50"
                              data-testid={`combined-bom-row-${index}`}
                            >
                              <td className="py-3 px-2 text-slate-600">{item.category}</td>
                              <td className="py-3 px-2">
                                {((item.datasheet_url || item.product_url) && !(item.datasheet_url || item.product_url || '').includes('yourwebsite.com')) ? (
                                  <a
                                    href={
                                      (item.datasheet_url || item.product_url).startsWith('http') 
                                        ? (item.datasheet_url || item.product_url)
                                        : `${BACKEND_URL}${item.datasheet_url || item.product_url}`
                                    }
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 underline font-medium cursor-pointer"
                                    title="View datasheet"
                                  >
                                    {item.sku}
                                  </a>
                                ) : (
                                  <span className="text-slate-900 font-medium">{item.sku}</span>
                                )}
                              </td>
                              <td className="py-3 px-2 text-slate-600">
                                {item.description}
                                {item.poa && (
                                  <a href={`mailto:${BASE_PRICING_EMAIL}?subject=${encodeURIComponent('Base pricing request - ' + (item.sku || ''))}&body=${encodeURIComponent('Please provide base pricing for ' + (item.sku || '') + ' (' + (item.description || '') + '), qty ' + (item.quantity || 1) + '.')}`}
                                    className="block text-xs text-blue-600 underline mt-0.5" data-testid="base-poa-email">Email ZKTeco for base pricing</a>
                                )}
                              </td>
                              <td className="py-3 px-2 text-right text-slate-900">{item.quantity}</td>
                              {showBomDistributorPrice && (
                                <>
                                  <td className="py-3 px-2 text-right text-purple-700">{item.poa ? 'POA' : `£${item.unit_price.toFixed(2)}`}</td>
                                  <td className="py-3 px-2 text-right text-purple-700 font-medium">{item.poa ? 'POA' : `£${item.line_total.toFixed(2)}`}</td>
                                </>
                              )}
                              {showBomInstallerPrice && (
                                <>
                                  <td className="py-3 px-2 text-right text-green-700 font-medium">{item.poa ? 'POA' : `£${item.installer_price.toFixed(2)}`}</td>
                                  <td className="py-3 px-2 text-right text-green-700 font-semibold">{item.poa ? 'POA' : `£${item.installer_total.toFixed(2)}`}</td>
                                </>
                              )}
                              {showBomEndUserPrice && (
                                <>
                                  <td className="py-3 px-2 text-right text-orange-700 font-medium">{item.poa ? 'POA' : `£${(item.end_user_price || 0).toFixed(2)}`}</td>
                                  <td className="py-3 px-2 text-right text-orange-700 font-semibold">{item.poa ? 'POA' : `£${(item.end_user_total || 0).toFixed(2)}`}</td>
                                </>
                              )}
                              <td className="print:hidden"></td>
                            </tr>
                          ))}
                          {/* Sundry Items in B.O.M */}
                          {sundryItems.map((item, index) => (
                            <tr 
                              key={`sundry-${index}`} 
                              className="border-b border-amber-200 bg-amber-50 hover:bg-amber-100"
                              data-testid={`sundry-bom-row-${index}`}
                            >
                              <td className="py-3 px-2 text-amber-700 font-medium">Sundry</td>
                              <td className="py-3 px-2 text-amber-800 font-mono text-xs">{item.sku || '-'}</td>
                              <td className="py-3 px-2 text-amber-800">{item.description}</td>
                              <td className="py-3 px-2 text-right text-amber-900">{item.quantity}</td>
                              {showBomDistributorPrice && (
                                <>
                                  <td className="py-3 px-2 text-right text-amber-700">£{item.unit_price.toFixed(2)}</td>
                                  <td className="py-3 px-2 text-right text-amber-700 font-medium">£{item.line_total.toFixed(2)}</td>
                                </>
                              )}
                              {showBomInstallerPrice && (
                                <>
                                  <td className="py-3 px-2 text-right text-amber-700">£{item.unit_price.toFixed(2)}</td>
                                  <td className="py-3 px-2 text-right text-amber-700 font-semibold">£{item.line_total.toFixed(2)}</td>
                                </>
                              )}
                              {showBomEndUserPrice && (
                                <>
                                  <td className="py-3 px-2 text-right text-amber-700">£{item.unit_price.toFixed(2)}</td>
                                  <td className="py-3 px-2 text-right text-amber-700 font-semibold">£{item.line_total.toFixed(2)}</td>
                                </>
                              )}
                              <td className="py-3 px-2 text-center print:hidden">
                                <div className="flex justify-center gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 w-7 p-0 text-amber-700 hover:text-amber-900"
                                    onClick={() => handleEditSundryItem(index)}
                                  >
                                    <FileText className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 w-7 p-0 text-red-600 hover:text-red-700"
                                    onClick={() => handleDeleteSundryItem(index)}
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          {(showBomDistributorPrice || showBomInstallerPrice || showBomEndUserPrice) ? (
                            <>
                              <tr className="border-t-2 border-slate-300 bg-slate-50">
                                <td colSpan="4" className="py-3 px-2 text-right font-semibold">Subtotals:</td>
                                {showBomDistributorPrice && (
                                  <>
                                    <td className="py-3 px-2 text-right font-semibold text-purple-700">Distributor:</td>
                                    <td className="py-3 px-2 text-right font-bold text-purple-700 text-lg" data-testid="trade-subtotal">£{result.trade_subtotal.toFixed(2)}</td>
                                  </>
                                )}
                                {showBomInstallerPrice && (
                                  <>
                                    <td className="py-3 px-2 text-right font-semibold text-green-700">{userRole === 'installer' ? 'Subtotal:' : 'Installer:'}</td>
                                    <td className="py-3 px-2 text-right font-bold text-green-700 text-lg" data-testid="installer-subtotal">
                                      £{result.installer_subtotal?.toFixed(2)}
                                    </td>
                                  </>
                                )}
                                {showBomEndUserPrice && (
                                  <>
                                    <td className="py-3 px-2 text-right font-semibold text-orange-700">End User:</td>
                                    <td className="py-3 px-2 text-right font-bold text-orange-700 text-lg">
                                      £{(result.combined_bom || []).reduce((sum, item) => sum + (item.end_user_total || 0), 0).toFixed(2)}
                                    </td>
                                  </>
                                )}
                                <td className="print:hidden"></td>
                              </tr>
                              <tr className="bg-slate-50">
                                <td colSpan="4" className="py-3 px-2 text-right font-medium text-slate-700">Delivery ({result.total_lanes} lanes):</td>
                                {showBomDistributorPrice && (
                                  <>
                                    <td className="py-3 px-2"></td>
                                    <td className="py-3 px-2 text-right font-semibold text-purple-700">£{result.delivery_cost.toFixed(2)}</td>
                                  </>
                                )}
                                {showBomInstallerPrice && (
                                  <>
                                    <td className="py-3 px-2"></td>
                                    <td className="py-3 px-2 text-right font-semibold text-green-700">£{result.delivery_cost_installer?.toFixed(2)}</td>
                                  </>
                                )}
                                {showBomEndUserPrice && (
                                  <>
                                    <td className="py-3 px-2"></td>
                                    <td className="py-3 px-2 text-right font-semibold text-orange-700">£{result.delivery_cost_installer?.toFixed(2)}</td>
                                  </>
                                )}
                                <td className="print:hidden"></td>
                              </tr>
                              <tr>
                                <td colSpan={4 + (showBomDistributorPrice ? 2 : 0) + (showBomInstallerPrice ? 2 : 0) + (showBomEndUserPrice ? 2 : 0) + 1} className="py-1 px-2 text-right text-xs text-slate-500 italic">
                                  (Delivery surcharges for multiple units may vary - please check with your ZKTeco Rep.)
                                </td>
                              </tr>
                              {result.price_modifier !== 0 && showBomInstallerPrice && (
                                <tr className="bg-blue-50">
                                  <td colSpan={4 + (showBomDistributorPrice ? 2 : 0)} className="py-3 px-2 text-right font-medium text-blue-800">
                                    Installer Discount ({result.price_modifier}%):
                                  </td>
                                  <td className="py-3 px-2"></td>
                                  <td className="py-3 px-2 text-right font-semibold text-blue-900">
                                    -£{result.modifier_amount.toFixed(2)}
                                  </td>
                                  <td className="print:hidden"></td>
                                </tr>
                              )}
                              <tr className="border-t-2 border-slate-400 bg-slate-900 text-white">
                                <td colSpan="4" className="py-4 px-2 text-right font-bold text-lg">Final Totals:</td>
                                {showBomDistributorPrice && (
                                  <>
                                    <td className="py-4 px-2 text-right font-bold text-purple-300">Distributor:</td>
                                    <td className="py-4 px-2 text-right font-bold text-xl text-purple-300">£{result.subtotal_with_delivery.toFixed(2)}</td>
                                  </>
                                )}
                                {showBomInstallerPrice && (
                                  <>
                                    <td className="py-4 px-2 text-right font-bold text-green-300">{userRole === 'installer' ? 'Your Total:' : 'Installer:'}</td>
                                    <td className="py-4 px-2 text-right font-bold text-2xl text-green-400" data-testid="final-total">£{result.final_installer_price?.toFixed(2)}</td>
                                  </>
                                )}
                                {showBomEndUserPrice && (
                                  <>
                                    <td className="py-4 px-2 text-right font-bold text-orange-300">End User:</td>
                                    <td className="py-4 px-2 text-right font-bold text-2xl text-orange-400" data-testid="final-enduser-total">
                                      £{((result.combined_bom || []).reduce((sum, item) => sum + (item.end_user_total || 0), 0) + (result.delivery_cost_installer || 0)).toFixed(2)}
                                    </td>
                                  </>
                                )}
                                <td className="print:hidden"></td>
                              </tr>
                              {sundryItems.length > 0 && (
                                <>
                                  <tr className="bg-amber-50">
                                    <td colSpan={4 + (showBomDistributorPrice ? 2 : 0) + (showBomInstallerPrice ? 1 : 0) + (showBomEndUserPrice ? 1 : 0)} className="py-3 px-2 text-right font-semibold text-amber-800">Sundry Items Total:</td>
                                    <td className="py-3 px-2 text-right font-bold text-amber-800">£{getSundryTotal().toFixed(2)}</td>
                                    <td className="print:hidden"></td>
                                  </tr>
                                  <tr className="bg-green-100 border-t-2 border-green-400">
                                    <td colSpan={4 + (showBomDistributorPrice ? 2 : 0) + (showBomInstallerPrice ? 1 : 0) + (showBomEndUserPrice ? 1 : 0)} className="py-3 px-2 text-right font-bold text-green-900">Grand Total (incl. Sundry):</td>
                                    <td className="py-3 px-2 text-right font-bold text-green-900 text-xl">
                                      £{showBomEndUserPrice 
                                        ? ((result.combined_bom || []).reduce((sum, item) => sum + (item.end_user_total || 0), 0) + (result.delivery_cost_installer || 0) + getSundryTotal()).toFixed(2)
                                        : (result.final_installer_price + getSundryTotal()).toFixed(2)}
                                    </td>
                                    <td className="print:hidden"></td>
                                  </tr>
                                </>
                              )}
                              {showBomDistributorPrice && (
                                <tr className="bg-green-50">
                                  <td colSpan={4 + (showBomDistributorPrice ? 2 : 0) + (showBomInstallerPrice ? 1 : 0)} className="py-3 px-2 text-right font-semibold text-green-800">Your Margin:</td>
                                  <td className="py-3 px-2 text-right font-bold text-green-800 text-lg">
                                    £{result.distributor_margin?.toFixed(2)}
                                    <span className="text-sm ml-2">
                                      ({((result.distributor_margin / result.final_installer_price) * 100).toFixed(1)}%)
                                    </span>
                                  </td>
                                  <td className="print:hidden"></td>
                                </tr>
                              )}
                            </>
                          ) : (
                            <>
                              {result.user_role === 'installer' ? (
                                <>
                                  {/* Installer view - only shows their cost price, no end user pricing */}
                                  <tr className="border-t-2 border-slate-300 bg-slate-50">
                                    <td colSpan="5" className="py-3 px-2 text-right font-semibold text-slate-900">Equipment Subtotal:</td>
                                    <td className="py-3 px-2 text-right font-bold text-slate-900 text-lg" data-testid="trade-subtotal">£{result.trade_subtotal.toFixed(2)}</td>
                                  </tr>
                                  <tr className="bg-slate-50">
                                    <td colSpan="5" className="py-3 px-2 text-right font-medium text-slate-700">Delivery ({result.total_lanes} lanes):</td>
                                    <td className="py-3 px-2 text-right font-semibold text-slate-900">£{result.delivery_cost.toFixed(2)}</td>
                                  </tr>
                                  <tr>
                                    <td colSpan="6" className="py-1 px-2 text-right text-xs text-slate-500 italic">
                                      (Delivery surcharges for multiple units may vary - please check with your ZKTeco Rep.)
                                    </td>
                                  </tr>
                                  <tr className="border-t-2 border-slate-400 bg-slate-900 text-white">
                                    <td colSpan="5" className="py-4 px-2 text-right font-bold text-lg">Your Total Cost:</td>
                                    <td className="py-4 px-2 text-right font-bold text-xl" data-testid="final-total">£{result.subtotal_with_delivery.toFixed(2)}</td>
                                  </tr>
                                  {sundryItems.length > 0 && (
                                    <>
                                      <tr className="bg-amber-50">
                                        <td colSpan="5" className="py-3 px-2 text-right font-semibold text-amber-800">Sundry Items Total:</td>
                                        <td className="py-3 px-2 text-right font-bold text-amber-800">£{getSundryTotal().toFixed(2)}</td>
                                      </tr>
                                      <tr className="bg-green-100 border-t-2 border-green-400">
                                        <td colSpan="5" className="py-3 px-2 text-right font-bold text-green-900">Grand Total (incl. Sundry):</td>
                                        <td className="py-3 px-2 text-right font-bold text-green-900 text-xl">£{(result.subtotal_with_delivery + getSundryTotal()).toFixed(2)}</td>
                                      </tr>
                                    </>
                                  )}
                                </>
                              ) : (
                                <>
                                  <tr className="border-t-2 border-slate-300 bg-slate-50">
                                    <td colSpan="5" className="py-3 px-2 text-right font-semibold text-slate-900">Subtotal:</td>
                                    <td className="py-3 px-2 text-right font-bold text-slate-900 text-lg" data-testid="trade-subtotal">£{result.trade_subtotal.toFixed(2)}</td>
                                    <td className="print:hidden"></td>
                                  </tr>
                                  <tr className="bg-slate-50">
                                    <td colSpan="5" className="py-3 px-2 text-right font-medium text-slate-700">Delivery ({result.total_lanes} lanes):</td>
                                    <td className="py-3 px-2 text-right font-semibold text-slate-900">£{result.delivery_cost.toFixed(2)}</td>
                                    <td className="print:hidden"></td>
                                  </tr>
                                  <tr>
                                    <td colSpan="7" className="py-1 px-2 text-right text-xs text-slate-500 italic">
                                      (Delivery surcharges for multiple units may vary - please check with your ZKTeco Rep.)
                                    </td>
                                  </tr>
                                  <tr className="border-t-2 border-slate-400 bg-slate-900 text-white">
                                    <td colSpan="5" className="py-4 px-2 text-right font-bold text-lg">FINAL TOTAL:</td>
                                    <td className="py-4 px-2 text-right font-bold text-2xl" data-testid="final-total">£{result.final_total.toFixed(2)}</td>
                                    <td className="print:hidden"></td>
                                  </tr>
                                  {sundryItems.length > 0 && (
                                    <>
                                      <tr className="bg-amber-50">
                                        <td colSpan="5" className="py-3 px-2 text-right font-semibold text-amber-800">Sundry Items Total:</td>
                                        <td className="py-3 px-2 text-right font-bold text-amber-800">£{getSundryTotal().toFixed(2)}</td>
                                        <td className="print:hidden"></td>
                                      </tr>
                                      <tr className="bg-green-100 border-t-2 border-green-400">
                                        <td colSpan="5" className="py-3 px-2 text-right font-bold text-green-900">Grand Total (incl. Sundry):</td>
                                        <td className="py-3 px-2 text-right font-bold text-green-900 text-xl">£{(result.final_total + getSundryTotal()).toFixed(2)}</td>
                                        <td className="print:hidden"></td>
                                      </tr>
                                    </>
                                  )}
                                </>
                              )}
                            </>
                          )}
                        </tfoot>
                      </table>
                    </div>
                  </CardContent>
                </Card>
                </div>
              </div>
            )}

            {!result && (
              <Card>
                <CardContent className="py-12">
                  <div className="text-center text-slate-500">
                    <Calculator className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>Configure your banks and calculate to see results</p>
                    <p className="text-sm mt-2">You can add multiple banks using the button on the left</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Status Update Dialog */}
      <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Project Status</DialogTitle>
            <DialogDescription>
              {newStatus === 'won' && 'Mark this project as Won?'}
              {newStatus === 'lost' && 'Mark this project as Lost?'}
              {newStatus === 'active' && 'Reopen this project as Active?'}
            </DialogDescription>
          </DialogHeader>
          
          {selectedConfig && (
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-slate-700">Project: {selectedConfig.project_name}</p>
                {selectedConfig.final_total && (
                  <p className="text-sm text-slate-500">Value: £{selectedConfig.final_total.toFixed(2)}</p>
                )}
              </div>
              
              {newStatus === 'lost' && (
                <div className="space-y-2">
                  <Label htmlFor="lost-reason">Reason (optional)</Label>
                  <Input
                    id="lost-reason"
                    placeholder="e.g., Budget constraints, competitor pricing..."
                    value={lostReason}
                    onChange={(e) => setLostReason(e.target.value)}
                  />
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpdateStatus}
              className={
                newStatus === 'won' ? 'bg-green-600 hover:bg-green-700' :
                newStatus === 'lost' ? 'bg-red-600 hover:bg-red-700' :
                ''
              }
            >
              {newStatus === 'won' && 'Mark as Won'}
              {newStatus === 'lost' && 'Mark as Lost'}
              {newStatus === 'active' && 'Reopen Project'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Project Support Dialog */}
      <ProjectSupportDialog
        open={showProjectSupport}
        onOpenChange={(open) => {
          setShowProjectSupport(open);
          // If user closes dialog without submitting, mark as declined
          if (!open) {
            setSupportDeclined(true);
          }
        }}
        quoteData={{
          project_name: projectName,
          quote_reference: quoteReference,
          banks: banks,
          combined_bom: result?.combined_bom,
          installer_price: result?.final_installer_price,
          installer_total: result?.final_installer_price,
          total: result?.final_total
        }}
        configuratorType="Comet Speedlane"
      />

      {/* Sundry Item Dialog */}
      <Dialog open={showSundryDialog} onOpenChange={setShowSundryDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingSundryIndex !== null ? 'Edit Sundry Item' : 'Add Sundry Item'}</DialogTitle>
            <DialogDescription>
              Add custom line items such as labour, cabling, commissioning, travel, etc.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="sundry-sku">SKU (optional)</Label>
              <Input
                id="sundry-sku"
                placeholder="e.g. LABOUR-001"
                value={sundryForm.sku}
                onChange={(e) => setSundryForm({...sundryForm, sku: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sundry-description">Description *</Label>
              <Input
                id="sundry-description"
                placeholder="e.g. Installation labour, Cabling, Travel"
                value={sundryForm.description}
                onChange={(e) => setSundryForm({...sundryForm, description: e.target.value})}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="sundry-quantity">Quantity *</Label>
                <Input
                  id="sundry-quantity"
                  type="number"
                  min="0.01"
                  step="0.01"
                  placeholder="1"
                  value={sundryForm.quantity}
                  onChange={(e) => setSundryForm({...sundryForm, quantity: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sundry-price">Unit Price (£) *</Label>
                <Input
                  id="sundry-price"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={sundryForm.unit_price}
                  onChange={(e) => setSundryForm({...sundryForm, unit_price: e.target.value})}
                />
              </div>
            </div>
            {sundryForm.quantity && sundryForm.unit_price && (
              <div className="bg-slate-50 p-3 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Line Total:</span>
                  <span className="font-semibold">
                    £{((parseFloat(sundryForm.quantity) || 0) * (parseFloat(sundryForm.unit_price) || 0)).toFixed(2)}
                  </span>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSundryDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveSundryItem}>
              {editingSundryIndex !== null ? 'Update' : 'Add Item'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ConfiguratorPage;